/**
 * Resumen del pedido con el total, y el cierre de la compra.
 *
 * El boton de pagar lleva a Webpay (PAG-01): al apretarlo se crea el pedido y
 * el navegador se redirige a la pasarela. Si algo en el formulario esta mal,
 * el clic no manda nada — marca los campos como tocados para que se vean los
 * errores, igual que un submit comun.
 *
 * Debajo queda WhatsApp, que sigue siendo la salida para quien prefiere
 * cerrar la compra por texto: el mensaje lleva el pedido completo con la
 * entrega y el documento elegidos (WSP-02, WSP-03).
 *
 * El costo del despacho que se ve aca es referencial mientras el negocio no
 * cargue su tabla de costos en el panel; el aviso lo
 * dice, en vez de presentar un numero como si estuviera confirmado.
 */
import type { Carro, TipoEntrega } from '@bandu/compartido';
import { Link } from 'react-router-dom';

import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { pesos } from '../../utilidades/formato';
import { nombreConVariante } from '../../utilidades/producto';
import { rutas } from '../../utilidades/rutas';
import { Icono } from '../comunes/Icono';

interface Props {
  carro: Carro;
  costoDespacho: number;
  despachoGratis: boolean;
  tipoEntrega: TipoEntrega;
  enlaceWhatsapp: string;
  alPagar: () => void;
  pagando: boolean;
  /** Cuando el stock ya se sabe insuficiente: no tiene sentido dejar apretar pagar. */
  pagoDeshabilitado: boolean;
  errorPago: string | null;
}

export function ResumenPedido({
  carro,
  costoDespacho,
  despachoGratis,
  tipoEntrega,
  enlaceWhatsapp,
  alPagar,
  pagando,
  pagoDeshabilitado,
  errorPago,
}: Props) {
  const total = carro.subtotal + costoDespacho;

  return (
    <aside className="ck-resumen" aria-labelledby="ck-resumen-titulo">
      <h2 id="ck-resumen-titulo">Tu pedido</h2>

      <ul className="ck-lineas">
        {carro.items.map((item) => (
          <li key={item.producto.sku}>
            <span>
              <span className="cant">{item.cantidad} × </span>
              {nombreConVariante(item.producto)}
            </span>
            <span className="monto">{pesos(item.producto.precio * item.cantidad)}</span>
          </li>
        ))}
      </ul>

      <div className="ck-separador" />

      <p className="ck-fila">
        <span>Subtotal</span>
        <span className="v">{pesos(carro.subtotal)}</span>
      </p>

      {tipoEntrega === 'coordinar' ? (
        // No es gratis: se cobra aparte, cuando se acuerda por telefono.
        <p className="ck-fila">
          <span>Entrega</span>
          <span className="v">Por coordinar</span>
        </p>
      ) : (
        <p className="ck-fila">
          <span>{tipoEntrega === 'retiro' ? 'Retiro en tienda' : 'Despacho'}</span>
          <span className={`v${costoDespacho === 0 ? ' gratis' : ''}`}>
            {costoDespacho === 0 ? 'Gratis' : pesos(costoDespacho)}
          </span>
        </p>
      )}

      <p className="ck-total">
        <span className="l">Total</span>
        <span className="v">{pesos(total)}</span>
      </p>

      <p className="ck-nota">
        Precios con IVA incluido.
        {tipoEntrega === 'despacho' && !despachoGratis
          ? ' El costo de despacho es referencial y se confirma al coordinar la entrega.'
          : ''}
        {tipoEntrega === 'coordinar'
          ? ' El total es solo de los productos: la entrega y su costo se acuerdan por teléfono.'
          : ''}
      </p>

      <div className="ck-acciones">
        {errorPago !== null ? (
          <p className="ck-alerta critica" role="alert">
            <Icono nombre="alerta" />
            <span>{errorPago}</span>
          </p>
        ) : null}

        <button
          type="button"
          className="btn btn-lg btn-primario btn-bloque"
          disabled={pagando || pagoDeshabilitado}
          onClick={alPagar}
        >
          <Icono nombre="escudo" />
          {pagando ? 'Conectando con Webpay…' : `Pagar ${pesos(total)}`}
        </button>

        <a
          className="btn btn-lg btn-wa btn-bloque"
          href={enlaceWhatsapp}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => registrarClicWhatsapp({ origen: 'checkout' })}
        >
          <Icono nombre="wa" />
          Enviar pedido por WhatsApp
        </a>

        <p className="carro-seguir">
          <Link to={rutas.carro()}>Volver al carro</Link>
        </p>
      </div>
    </aside>
  );
}
